"use client"

import { useAuth } from "@clerk/nextjs"
import { useCallback, useEffect, useState } from "react"

import { isApiSuccessBody } from "@/lib/api/api-envelope"
import type { ApiPlanSummaryPayload } from "@/lib/api/billing-contract"

export function useBillingSummary() {
  const { isSignedIn } = useAuth()
  const [data, setData] = useState<ApiPlanSummaryPayload | null>(null)
  const [loading, setLoading] = useState(true)

  const refetch = useCallback(async () => {
    if (!isSignedIn) {
      setData(null)
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const res = await fetch("/api/billing/summary", { cache: "no-store" })
      const body: unknown = await res.json()
      if (res.ok && isApiSuccessBody<ApiPlanSummaryPayload>(body)) {
        setData(body.data)
      } else {
        setData(null)
      }
    } catch {
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [isSignedIn])

  useEffect(() => {
    void refetch()
  }, [refetch])

  useEffect(() => {
    if (!isSignedIn) return
    const onVis = () => {
      if (document.visibilityState === "visible") void refetch()
    }
    document.addEventListener("visibilitychange", onVis)
    return () => document.removeEventListener("visibilitychange", onVis)
  }, [isSignedIn, refetch])

  return { data, loading, refetch }
}
